import React from 'react';
import { Segment, Pagination } from 'semantic-ui-react';
import { PhoneInventoryDataTable } from './PhoneInventoryDataTable';
import { PhoneInventoryRow } from './PhoneInventoryRow';
import { PhoneInventoryFilter } from './PhoneInventoryFilter';
import { PhoneInventoryStats } from './PhoneInventoryStats';
import ConfirmationModal from '../shared/ConfirmationModal';
import './PhoneInventoryList.css';

const tableHeader = [
  {
    title: '',
    width: '1',
    sort: '',
    checkbox: true,
  },
  {
    title: 'Phone Number',
    width: '3',
    sort: '',
  },
  {
    title: 'Type',
    width: '2',
    sort: 'number_type',
  },
  {
    title: 'Status',
    width: '2',
    sort: 'assignment_status',
  },
  {
    title: 'Voice Capability',
    width: '2',
    sort: '',
  },
  {
    title: 'SMS Capability',
    width: '2',
    sort: '',
  },
  {
    title: 'Assignment',
    width: '2',
    sort: '',
  },
  {
    title: 'Action',
    width: '2',
    sort: '',
  },
];

const queryParams = ['_limit', '_order', '_sort', 'q', '_page'];

export default class PhoneInventoryList extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      phones: [],
      _sort: 'phone_number',
      _page: 1,
      _order: 'asc',
      _limit: 10,
      q: '',
      assignment_status: 'all',
      number_type: 'all',
      totalCount: 0,
      summary: null,
      loading: false,
      phoneRows: null,
      showAssignConfirm: false,
      showBulkAssignConfirm: false,
      showUnassignConfirm: false,
      phoneToAssign: null,
      phoneToUnassign: null,
      showSmsAssignConfirm: false,
      showSmsUnassignConfirm: false,
      phoneToSmsAssign: null,
      phoneToSmsUnassign: null,
      actionLoading: false,
      successMessage: null,
    };
  }

  componentDidMount() {
    this.loadData({});
  }

  componentDidUpdate(prevProps) {
    // Reload data if campaignData changes
    if (prevProps.campaignData !== this.props.campaignData) {
      this.loadData({});
    }
  }

  static directionConverter(order) {
    if (order === 'asc') {
      return 'ascending';
    } else if (order === 'desc') {
      return 'descending';
    } else {
      return null;
    }
  }

  handleSort = (clickedColumn) => {
    const { _sort, _order } = this.state;

    let newOrder = _order === 'asc' ? 'desc' : 'asc';
    if (_sort !== clickedColumn) {
      newOrder = 'asc';
    }

    this.loadData({
      _sort: clickedColumn,
      _page: 1,
      _order: newOrder,
    });
  };

  onChangeLimit = (event, data) => {
    if (data.value !== this.state._limit) {
      this.loadData({ _limit: data.value, _page: 1 });
    }
  };

  onSubmitFilter = (filterType, value) => {
    const newFilters = { _page: 1 };

    if (filterType === 'q') {
      newFilters.q = value;
    } else if (filterType === 'assignment_status') {
      newFilters.assignment_status = value;
    } else if (filterType === 'number_type') {
      newFilters.number_type = value;
    }

    this.setState(newFilters, () => {
      this.loadData(newFilters);
    });
  };


  onChangePage = (event, data) => {
    const { activePage } = data;
    if (activePage !== this.state._page) {
      this.loadData({ _page: activePage });
    }
  };

  loadData = async (params) => {
    const newState = Object.assign({}, this.state, params, { loading: true });
    queryParams.forEach(function (element) {
      if (!(element in params)) {
        params[element] = newState[element];
      }
    });

    this.setState(newState, async () => {
      try {
        let phones = [];
        let totalCount = 0;

        // Load phones from API
        if (this.props.onLoadVoicePhones) {
          const data = await this.props.onLoadVoicePhones(params);
          phones = data.phones || [];
          totalCount = data.totalCount || phones.length;
          if (data.summary) {
            this.setState({ summary: data.summary });
          }
        }

        // Normalize phone data structure
        let normalizedPhones = phones.map(phone => ({
          ...phone,
          id: phone.id || phone.phone_number_sid || phone.phone_sid,
          phone_sid: phone.phone_sid || phone.phone_number_sid,
          twilio_phone: phone.twilio_phone || phone.phone_number,
        }));

        // Apply client-side filtering
        let filteredPhones = normalizedPhones;

        // Search filter
        if (params.q && params.q.trim()) {
          filteredPhones = filteredPhones.filter((p) =>
            (p.phone_number || '').toLowerCase().includes(params.q.toLowerCase())
          );
        }

        // Assignment status filter
        if (params.assignment_status && params.assignment_status !== 'all') {
          filteredPhones = filteredPhones.filter((p) => {
            const isAssignedToBusiness = p.assignment_status === 'assigned' || p.assignment_sid;
            const isAssignedToSms = p.is_assigned; // A2P campaign assignment

            switch (params.assignment_status) {
              case 'business_profile':
                // Assigned to Business Profile (may or may not be assigned to SMS)
                return isAssignedToBusiness;
              case 'sms_campaign':
                // Assigned to SMS Campaign (may or may not be assigned to Business)
                return isAssignedToSms;
              case 'both':
                // Assigned to both Business Profile and SMS Campaign
                return isAssignedToBusiness && isAssignedToSms;
              case 'not_assigned':
                // Not assigned to either Business Profile or SMS Campaign
                return !isAssignedToBusiness && !isAssignedToSms;
              default:
                return true;
            }
          });
        }

        // Number type filter
        if (params.number_type && params.number_type !== 'all') {
          filteredPhones = filteredPhones.filter((p) => {
            const type = (p.number_type || '').toLowerCase();
            return type === params.number_type.toLowerCase();
          });
        }

        // Apply sorting
        if (params._sort) {
          filteredPhones.sort((a, b) => {
            const aVal = a[params._sort] || '';
            const bVal = b[params._sort] || '';
            if (params._order === 'asc') {
              return aVal > bVal ? 1 : -1;
            } else {
              return aVal < bVal ? 1 : -1;
            }
          });
        }

        let phoneRowsTemp;
        if (filteredPhones.length > 0) {
          phoneRowsTemp = filteredPhones.map((phone, index) => (
            <PhoneInventoryRow
              key={phone.id || phone.phone_number_sid || index}
              phone={phone}
              onCheckboxChange={this.checkboxChangeEvent}
              onAssignPhone={this.handleAssignPhone}
              onUnassignPhone={this.handleUnassignPhone}
              onAssignPhoneToSms={this.handleAssignPhoneToSms}
              onUnassignPhoneFromSms={this.handleUnassignPhoneFromSms}
              loading={this.state.actionLoading}
              campaignData={this.props.campaignData}
            />
          ));
        } else {
          phoneRowsTemp = null;
        }

        this.setState({
          phoneRows: phoneRowsTemp,
          totalCount: filteredPhones.length,
          phones: filteredPhones,
          loading: false,
        });
      } catch (err) {
        console.error('Error loading phones:', err);
        this.setState({ loading: false });
      }
    });
  };

  checkAll = (e) => {
    let phoneList = this.state.phones;
    phoneList.map(function (phone, index) {
      phone['checked'] = e.target.checked;
    });

    // Regenerate phone rows to reflect checkbox changes
    let phoneRowsTemp = phoneList.map((phone, index) => (
      <PhoneInventoryRow
        key={phone.id || phone.phone_number_sid || index}
        phone={phone}
        onCheckboxChange={this.checkboxChangeEvent}
        onAssignPhone={this.handleAssignPhone}
        onUnassignPhone={this.handleUnassignPhone}
        onAssignPhoneToSms={this.handleAssignPhoneToSms}
        onUnassignPhoneFromSms={this.handleUnassignPhoneFromSms}
        loading={this.state.actionLoading}
        campaignData={this.props.campaignData}
      />
    ));

    this.setState({
      phones: phoneList,
      phoneRows: phoneRowsTemp,
    });
  };

  checkboxChangeEvent = (phoneId) => {
    let phoneList = this.state.phones;
    let recordIndex = phoneList.findIndex(
      (phone) =>
        (phone.id || phone.phone_number_sid || phone.phone_sid) === phoneId
    );

    if (recordIndex !== -1) {
      phoneList[recordIndex]['checked'] = !phoneList[recordIndex]['checked'];

      // Regenerate phone rows to reflect checkbox changes
      let phoneRowsTemp = phoneList.map((phone, index) => (
        <PhoneInventoryRow
          key={phone.id || phone.phone_number_sid || index}
          phone={phone}
          onCheckboxChange={this.checkboxChangeEvent}
          onAssignPhone={this.handleAssignPhone}
          onUnassignPhone={this.handleUnassignPhone}
          onAssignPhoneToSms={this.handleAssignPhoneToSms}
          onUnassignPhoneFromSms={this.handleUnassignPhoneFromSms}
          loading={this.state.actionLoading}
          campaignData={this.props.campaignData}
        />
      ));

      this.setState({
        phones: phoneList,
        phoneRows: phoneRowsTemp,
      });
    }
  };

  handleAssignPhone = (phone) => {
    this.setState({
      phoneToAssign: phone,
      showAssignConfirm: true,
    });
  };

  handleUnassignPhone = (phone) => {
    this.setState({
      phoneToUnassign: phone,
      showUnassignConfirm: true,
    });
  };

  handleAssignPhoneToSms = (phone) => {
    this.setState({
      phoneToSmsAssign: phone,
      showSmsAssignConfirm: true,
    });
  };

  handleUnassignPhoneFromSms = (phone) => {
    this.setState({
      phoneToSmsUnassign: phone,
      showSmsUnassignConfirm: true,
    });
  };

  handleBulkAssign = () => {
    const selectedPhones = this.state.phones.filter((p) => p.checked);
    if (selectedPhones.length === 0) {
      alert('Please select at least one phone number');
      return;
    }

    // Check assignment status of selected phones
    const assignedPhones = selectedPhones.filter(
      (p) => p.assignment_status === 'assigned' || p.assignment_sid
    );
    const unassignedPhones = selectedPhones.filter(
      (p) => !(p.assignment_status === 'assigned' || p.assignment_sid)
    );

    // Mixed selection - both assigned and unassigned
    if (assignedPhones.length > 0 && unassignedPhones.length > 0) {
      alert(
        `You have selected both assigned and unassigned phone numbers.\n\nPlease select either:\n• Only assigned numbers (to unassign them)\n• Only unassigned numbers (to assign them)`
      );
      return;
    }

    // Determine action type and show appropriate confirmation
    const isUnassignAction = assignedPhones.length > 0;
    this.setState({
      showBulkAssignConfirm: true,
      bulkActionType: isUnassignAction ? 'unassign' : 'assign'
    });
  };

  handleConfirmAssign = async () => {
    if (this.state.actionLoading) return;

    const phone = this.state.phoneToAssign;
    this.setState({ actionLoading: true });

    try {
      if (this.props.onAssignPhone) {
        await this.props.onAssignPhone(phone);
      }
      this.setState({
        showAssignConfirm: false,
        phoneToAssign: null,
        actionLoading: false,
      });
      this.loadData({});
    } catch (err) {
      console.error('Assign error:', err);
      this.setState({ actionLoading: false });
    }
  };

  handleConfirmUnassign = async () => {
    if (this.state.actionLoading) return;

    const phone = this.state.phoneToUnassign;
    this.setState({ actionLoading: true });

    try {
      if (this.props.onUnassignPhone) {
        await this.props.onUnassignPhone(phone);
      }
      this.setState({
        showUnassignConfirm: false,
        phoneToUnassign: null,
        actionLoading: false,
      });
      this.loadData({});
    } catch (err) {
      console.error('Unassign error:', err);
      this.setState({ actionLoading: false });
    }
  };

  handleConfirmSmsAssign = async () => {
    if (this.state.actionLoading) return;

    const phone = this.state.phoneToSmsAssign;
    this.setState({ actionLoading: true });

    try {
      if (this.props.onAssignPhoneToSms) {
        await this.props.onAssignPhoneToSms(phone);
      }
      this.setState({
        showSmsAssignConfirm: false,
        phoneToSmsAssign: null,
        actionLoading: false,
      });
      this.loadData({});
    } catch (err) {
      console.error('SMS Assign error:', err);
      this.setState({ actionLoading: false });
    }
  };

  handleConfirmSmsUnassign = async () => {
    if (this.state.actionLoading) return;

    const phone = this.state.phoneToSmsUnassign;
    this.setState({ actionLoading: true });

    try {
      if (this.props.onUnassignPhoneFromSms) {
        await this.props.onUnassignPhoneFromSms(phone);
      }
      this.setState({
        showSmsUnassignConfirm: false,
        phoneToSmsUnassign: null,
        actionLoading: false,
      });
      this.loadData({});
    } catch (err) {
      console.error('SMS Unassign error:', err);
      this.setState({ actionLoading: false });
    }
  };

  handleConfirmBulkAssign = async () => {
    if (this.state.actionLoading) return;

    const selectedPhones = this.state.phones.filter((p) => p.checked);
    const isUnassignAction = this.state.bulkActionType === 'unassign';
    const phoneCount = selectedPhones.length;
    this.setState({ actionLoading: true });

    try {
      if (isUnassignAction) {
        // If bulk unassign prop exists, use it; otherwise call single unassign for each phone
        if (this.props.onBulkUnassignPhones) {
          await this.props.onBulkUnassignPhones(selectedPhones);
        } else if (this.props.onUnassignPhone) {
          // Fall back to calling single unassign for each phone
          for (const phone of selectedPhones) {
            await this.props.onUnassignPhone(phone);
          }
        }
      } else {
        if (this.props.onBulkAssignPhones) {
          await this.props.onBulkAssignPhones(selectedPhones);
        }
      }

      // Show success message
      const action = isUnassignAction ? 'unassigned' : 'assigned';
      const successMessage = `${phoneCount} phone number${phoneCount > 1 ? 's' : ''} ${action} successfully`;

      this.setState({
        showBulkAssignConfirm: false,
        bulkActionType: null,
        actionLoading: false,
        successMessage: successMessage,
      });

      // Clear success message after 5 seconds
      setTimeout(() => {
        this.setState({ successMessage: null });
      }, 5000);

      this.loadData({});
    } catch (err) {
      console.error(`Bulk ${isUnassignAction ? 'unassign' : 'assign'} error:`, err);
      this.setState({ actionLoading: false });
    }
  };

  render() {
    const selectedCount = this.state.phones.filter((p) => p.checked).length;
    const selectedPhones = this.state.phones.filter((p) => p.checked);
    const selectedAssignedCount = selectedPhones.filter((p) => p.assignment_status === 'assigned' || p.assignment_sid).length;
    const selectedUnassignedCount = selectedCount - selectedAssignedCount;

    const { summary } = this.state;
    const assignedCount   = summary ? summary.business_assigned : this.state.phones.filter((p) => p.assignment_status === 'assigned' || p.assignment_sid).length;
    const a2pCount        = summary ? summary.sms_assigned      : this.state.phones.filter((p) => p.is_assigned).length;
    const totalStatsCount = summary ? summary.total             : this.state.totalCount;
    const unassignedCount = summary ? summary.unassigned        : totalStatsCount - assignedCount;

    // Determine button text based on selection
    let bulkActionText = 'Bulk Assign';
    if (selectedAssignedCount > 0 && selectedUnassignedCount === 0) {
      bulkActionText = 'Bulk Unassign';
    } else if (selectedAssignedCount > 0 && selectedUnassignedCount > 0) {
      bulkActionText = 'Bulk Action';
    }

    return (
      <div className="phone-inventory-list">
        {this.state.successMessage && (
          <div style={{
            padding: '12px 20px',
            marginBottom: '20px',
            backgroundColor: '#d4edda',
            color: '#155724',
            border: '1px solid #c3e6cb',
            borderRadius: '4px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <i className="la la-check-circle" style={{ fontSize: '20px' }}></i>
              <span>{this.state.successMessage}</span>
            </div>
            <button
              onClick={() => this.setState({ successMessage: null })}
              style={{
                background: 'none',
                border: 'none',
                color: '#155724',
                cursor: 'pointer',
                fontSize: '18px',
                padding: '0 5px'
              }}
            >
              ×
            </button>
          </div>
        )}

        <div className="phone-inventory-header-row">
          <PhoneInventoryFilter
            filter={this.state.q}
            onSubmitFilter={this.onSubmitFilter}
            totalCount={this.state.totalCount}
          />

          <PhoneInventoryStats
            totalCount={totalStatsCount}
            assignedCount={assignedCount}
            unassignedCount={unassignedCount}
            a2pCount={a2pCount}
          />
        </div>

        {selectedCount > 0 && (
          <div className="bulk-actions-bar">
            <span className="bulk-selection-count">
              {selectedCount} Phone Number{selectedCount > 1 ? 's' : ''} Selected
            </span>
            <div className="bulk-actions">
              <button
                className="btn linear-gradient zy-save-btn ml-2"
                onClick={this.handleBulkAssign}
              >
                {bulkActionText}
              </button>
              <button
                className="btn linear-gradient-cancel yr-cancelbtn mg-r"
                onClick={this.checkAll}
              >
                Clear Selection
              </button>
            </div>
          </div>
        )}

        <Segment style={{ width: '100%' }}>
          {this.state.loading && <div className="ui loading form"></div>}

          <PhoneInventoryDataTable
            phones={this.state.phones}
            tableHeader={tableHeader}
            phoneRows={this.state.phoneRows}
            column={this.state._sort}
            direction={PhoneInventoryList.directionConverter(this.state._order)}
            handleSort={this.handleSort}
            checkAll={this.checkAll}
            loading={this.state.loading}
          />
        </Segment>

        <div className="pagination-wrapper">
          <span>
            Showing 1 - {Math.min(this.state._limit, this.state.totalCount)} of{' '}
            {this.state.totalCount}
          </span>
          <Pagination
            totalPages={Math.ceil(this.state.totalCount / this.state._limit)}
            activePage={this.state._page}
            onPageChange={this.onChangePage}
          />
        </div>

        {/* Confirmation Modals */}
        <ConfirmationModal
          isOpen={this.state.showAssignConfirm}
          onClose={() =>
            this.setState({ showAssignConfirm: false, phoneToAssign: null })
          }
          onConfirm={this.handleConfirmAssign}
          title="Assign Phone Number"
          message="Are you sure you want to assign this phone number?"
          confirmText="Assign"
          cancelText="Cancel"
          loading={this.state.actionLoading}
        />

        <ConfirmationModal
          isOpen={this.state.showBulkAssignConfirm}
          onClose={() => this.setState({ showBulkAssignConfirm: false, bulkActionType: null })}
          onConfirm={this.handleConfirmBulkAssign}
          title={this.state.bulkActionType === 'unassign' ? 'Bulk Unassign Phone Numbers' : 'Bulk Assign Phone Numbers'}
          message={this.state.bulkActionType === 'unassign' ? 'Are you sure you want to unassign selected phone numbers?' : 'Are you sure you want to assign selected phone numbers?'}
          confirmText={this.state.bulkActionType === 'unassign' ? 'Unassign Selected' : 'Assign Selected'}
          cancelText="Cancel"
          loading={this.state.actionLoading}
        />

        <ConfirmationModal
          isOpen={this.state.showUnassignConfirm}
          onClose={() =>
            this.setState({ showUnassignConfirm: false, phoneToUnassign: null })
          }
          onConfirm={this.handleConfirmUnassign}
          title="Unassign Phone Number"
          message="Are you sure you want to unassign this phone number from Business Profile?"
          confirmText="Unassign"
          cancelText="Cancel"
          loading={this.state.actionLoading}
        />

        <ConfirmationModal
          isOpen={this.state.showSmsAssignConfirm}
          onClose={() =>
            this.setState({ showSmsAssignConfirm: false, phoneToSmsAssign: null })
          }
          onConfirm={this.handleConfirmSmsAssign}
          title="Assign Phone to SMS Campaign"
          message="Are you sure you want to assign this phone number to the A2P SMS Campaign?"
          confirmText="Assign to SMS"
          cancelText="Cancel"
          loading={this.state.actionLoading}
        />

        <ConfirmationModal
          isOpen={this.state.showSmsUnassignConfirm}
          onClose={() =>
            this.setState({ showSmsUnassignConfirm: false, phoneToSmsUnassign: null })
          }
          onConfirm={this.handleConfirmSmsUnassign}
          title="Unassign Phone from SMS Campaign"
          message="Are you sure you want to unassign this phone number from the A2P SMS Campaign?"
          confirmText="Unassign from SMS"
          cancelText="Cancel"
          loading={this.state.actionLoading}
        />
      </div>
    );
  }
}
